import React, { useEffect, useState, useCallback } from "react";
import { TL } from "../i18n";
import client from "../lib/api";
import { Button, Input, Select } from "./ui";
import { Plus, Pencil, Trash2, Check, X, Settings2 } from "lucide-react";

// Loads/saves a user's per-app custom option lists from /api/options/{app}.
export function useOptionLists(app) {
  const [lists, setLists] = useState({});
  const reload = useCallback(() => {
    if (!app) return;
    client.get(`/options/${app}`).then((r) => setLists(r.data || {})).catch(() => {});
  }, [app]);
  useEffect(() => { reload(); }, [reload]);
  const saveList = useCallback(async (key, values) => {
    setLists((l) => ({ ...l, [key]: values }));
    await client.put(`/options/${app}/${key}`, { values }).catch(() => {});
  }, [app]);
  return { lists, saveList, reload };
}

// A dropdown that merges built-in presets with the user's own options, with inline management.
export function OptionSetSelect({ label, value, onChange, presets = [], customKey, lists, saveList, maxCustom = 8, testid }) {
  const [manage, setManage] = useState(false);
  const [draft, setDraft] = useState("");
  const custom = lists[customKey] || [];
  const addCustom = () => {
    const v = draft.trim().slice(0, 40);
    if (!v || presets.includes(v) || custom.includes(v) || custom.length >= maxCustom) return;
    saveList(customKey, [...custom, v]); setDraft("");
  };
  const removeCustom = (v) => saveList(customKey, custom.filter((x) => x !== v));
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-medium opacity-70">{label}</span>
        <button type="button" onClick={() => setManage((m) => !m)} data-testid={`${testid}-manage`}
          className="inline-flex items-center gap-1 text-[11px] text-[var(--primary)] hover:underline">
          <Settings2 size={11} /> {manage ? "Done" : "Customize"}
        </button>
      </div>
      <select value={value || ""} onChange={onChange} data-testid={testid}
        className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm outline-none focus:border-[var(--primary)]">
        <option value="">—</option>
        {presets.map((o) => <option key={o} value={o}>{o}</option>)}
        {custom.length > 0 && <optgroup label={TL("Your options")}>{custom.map((o) => <option key={o} value={o}>{o}</option>)}</optgroup>}
      </select>
      {manage && (
        <div className="mt-2 rounded-md border border-black/10 dark:border-white/10 p-2" data-testid={`${testid}-manage-panel`}>
          <div className="mb-1 text-[11px] opacity-60">Your options ({custom.length}/{maxCustom})</div>
          <div className="mb-2 flex flex-wrap gap-1">
            {custom.map((o) => (
              <span key={o} className="inline-flex items-center gap-1 rounded-full bg-black/5 dark:bg-white/10 px-2 py-0.5 text-xs" data-testid={`${testid}-chip-${o}`}>
                {o}<button type="button" onClick={() => removeCustom(o)} className="opacity-60 hover:text-[#DA1E28]"><X size={12} /></button>
              </span>
            ))}
            {custom.length === 0 && <span className="text-xs opacity-40">{TL("None yet.")}</span>}
          </div>
          {custom.length < maxCustom && (
            <div className="flex gap-2">
              <input value={draft} maxLength={40} onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustom())}
                placeholder={TL("Add your own…")} data-testid={`${testid}-add-input`}
                className="flex-1 rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-2 py-1 text-sm" />
              <Button variant="ghost" onClick={addCustom} data-testid={`${testid}-add-btn`}><Plus size={14} /></Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// A dropdown of user-defined accounts (name + number, each up to 15 chars) with inline management.
export function AccountSelect({ label, value, onChange, customKey = "accounts", lists, saveList, testid }) {
  const [manage, setManage] = useState(false);
  const [name, setName] = useState("");
  const [number, setNumber] = useState("");
  const accounts = lists[customKey] || [];
  const fmt = (a) => `${a.name} (${a.number})`;
  const addAccount = () => {
    const n = name.trim().slice(0, 15), num = number.trim().slice(0, 15);
    if (!n || !num || accounts.some((a) => a.name === n && a.number === num)) return;
    saveList(customKey, [...accounts, { name: n, number: num }]); setName(""); setNumber("");
  };
  const removeAccount = (a) => saveList(customKey, accounts.filter((x) => !(x.name === a.name && x.number === a.number)));
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-medium opacity-70">{label}</span>
        <button type="button" onClick={() => setManage((m) => !m)} data-testid={`${testid}-manage`}
          className="inline-flex items-center gap-1 text-[11px] text-[var(--primary)] hover:underline">
          <Settings2 size={11} /> {manage ? "Done" : "Manage accounts"}
        </button>
      </div>
      <select value={value || ""} onChange={onChange} data-testid={testid}
        className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm outline-none focus:border-[var(--primary)]">
        <option value="">{TL("— select account —")}</option>
        {accounts.map((a) => <option key={fmt(a)} value={fmt(a)}>{fmt(a)}</option>)}
      </select>
      {manage && (
        <div className="mt-2 rounded-md border border-black/10 dark:border-white/10 p-2" data-testid={`${testid}-manage-panel`}>
          <div className="mb-2 space-y-1">
            {accounts.map((a) => (
              <div key={fmt(a)} className="flex items-center justify-between rounded bg-black/5 dark:bg-white/10 px-2 py-1 text-xs" data-testid={`${testid}-acct-${a.name}`}>
                <span>{a.name} · <span className="opacity-60">{a.number}</span></span>
                <button type="button" onClick={() => removeAccount(a)} className="opacity-60 hover:text-[#DA1E28]"><Trash2 size={12} /></button>
              </div>
            ))}
            {accounts.length === 0 && <span className="text-xs opacity-40">{TL("No accounts yet.")}</span>}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input value={name} maxLength={15} onChange={(e) => setName(e.target.value)} placeholder={TL("Account name (15)")} data-testid={`${testid}-name`}
              className="rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-2 py-1 text-sm" />
            <input value={number} maxLength={15} onChange={(e) => setNumber(e.target.value)} placeholder={TL("Account number (15)")} data-testid={`${testid}-number`}
              className="rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-2 py-1 text-sm" />
          </div>
          <div className="mt-2 flex justify-end">
            <Button variant="ghost" onClick={addAccount} data-testid={`${testid}-add`}><Plus size={14} /> {TL("Add account")}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

const RECUR = [["once", "One-time"], ["daily", "Daily"], ["weekly", "Weekly"], ["monthly", "Monthly"], ["quarterly", "Quarterly"], ["yearly", "Yearly"]];

// A combined date-picker + recurrence selector. Value is stored as "YYYY-MM-DD|recur".
export function ScheduleField({ label, value, onChange, testid }) {
  const [date, recur] = String(value || "").split("|");
  const emit = (d, r) => onChange({ target: { value: `${d || ""}|${r || "once"}` } });
  return (
    <div>
      <span className="mb-1 block text-xs font-medium opacity-70">{label}</span>
      <div className="grid grid-cols-2 gap-2">
        <input type="date" value={date || ""} onChange={(e) => emit(e.target.value, recur || "once")} data-testid={`${testid}-date`}
          className="rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-2 py-2 text-sm" />
        <select value={recur || "once"} onChange={(e) => emit(date || "", e.target.value)} data-testid={`${testid}-recur`}
          className="rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-2 py-2 text-sm">
          {RECUR.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
    </div>
  );
}

export function fmtSchedule(v) {
  if (!v) return "—";
  const [date, recur] = String(v).split("|");
  if (!date) return "—";
  const r = (RECUR.find((x) => x[0] === recur) || [])[1] || "One-time";
  return `${date} · ${r}`;
}
